import { PORTAL_WEB_PATHS } from '@fos/shared/contracts/portal';
import { isClientRole } from '@fos/shared/enums';
import { LogOut } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { NetworkError } from '../lib/api';
import { useSession } from '../lib/auth';
import { useOnline } from '../lib/useOnline';
import { Button } from '../ui/Button';
import { LogoSymbol } from '../ui/Logo';
import { Spinner } from '../ui/Spinner';
import { EmptyState, ErrorState } from '../ui/States';
import { useSignOut } from './layout/UserMenus';

/** Ekran startowy podczas sprawdzania sesji. */
export function SplashScreen() {
  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-app"
      role="status"
      aria-label="Wczytywanie"
    >
      <LogoSymbol size={32} />
      <Spinner size={18} className="text-fg-muted" />
    </div>
  );
}

function CenteredScreen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center bg-app px-4">{children}</div>;
}

/**
 * Brak połączenia albo API nie odpowiada (sesji nie da się sprawdzić). Powrót sieci (zdarzenie `online` po stanie
 * offline) sam ponawia sprawdzenie sesji — także gdy aplikacja (PWA) wystartowała bez sieci: `onlineManager`
 * React Query startuje jako online i wtedy sam nie odświeży zapytania (przegląd Fali 5).
 */
export function OfflineScreen({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  const online = useOnline();
  const retryRef = useRef(onRetry);
  retryRef.current = onRetry;
  const wasOffline = useRef(!online);
  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      return;
    }
    if (wasOffline.current) {
      wasOffline.current = false;
      retryRef.current();
    }
  }, [online]);
  return (
    <CenteredScreen>
      <div className="w-full max-w-[400px] rounded-xl border border-line bg-panel">
        <ErrorState
          offline
          title={online ? 'Serwer nie odpowiada' : 'Brak połączenia'}
          description={
            online
              ? 'Nie udało się połączyć z Fravity OS. Spróbuj za chwilę.'
              : 'Sprawdź internet. Wrócimy do pracy, gdy połączenie się pojawi.'
          }
          onRetry={onRetry}
          retrying={retrying}
        />
      </div>
    </CenteredScreen>
  );
}

/**
 * Strażnik tras aplikacji: bez sesji → /login?next=…; etap inny niż „active” (2FA w etapie końcowym) → /login;
 * brak workspace'u → spokojny komunikat.
 */
export function RequireAuth() {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) return <SplashScreen />;
  if (session.isError) {
    if (session.error instanceof NetworkError) {
      return <OfflineScreen onRetry={() => void session.refetch()} retrying={session.isFetching} />;
    }
    return (
      <CenteredScreen>
        <div className="w-full max-w-[400px] rounded-xl border border-line bg-panel">
          <ErrorState onRetry={() => void session.refetch()} retrying={session.isFetching} />
        </div>
      </CenteredScreen>
    );
  }

  const data = session.data;
  if (!data.authenticated || data.stage !== 'active') {
    const next = `${location.pathname}${location.search}`;
    const search = next && next !== '/' && next !== '/dzis' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/login${search}`} replace />;
  }
  if (!data.workspace || !data.membership || !data.access) return <NoWorkspaceScreen />;
  return <Outlet />;
}

function NoWorkspaceScreen() {
  const signOut = useSignOut();
  return (
    <CenteredScreen>
      <div className="w-full max-w-[420px] rounded-xl border border-line bg-panel">
        <EmptyState
          title="Nie masz jeszcze dostępu do żadnego workspace’u"
          description="Poproś administratora o zaproszenie. Po jego akceptacji zaloguj się ponownie."
          action={
            <Button variant="secondary" icon={LogOut} onClick={signOut}>
              Wyloguj
            </Button>
          }
        />
      </div>
    </CenteredScreen>
  );
}

/** Osoba klienta (rola `client`) w aktywnym workspace'ie — widzi tylko portal. */
function useIsPortalSession(): boolean {
  const data = useSession().data;
  return Boolean(data?.authenticated && isClientRole(data.membership?.role));
}

/**
 * Trasy zespołu (powłoka aplikacji, zgoda konektora): osoba klienta (DECISIONS 2026-10-17 [P] „Model portalu
 * klienta” nr 1) trafia z każdej ścieżki do portalu (`/portal`). Backend i tak odmawia (bramka „domyślnie nie”);
 * strażnik oszczędza ekranów z błędami. Wewnątrz `RequireAuth`.
 */
export function RequireTeam() {
  if (useIsPortalSession()) return <Navigate to={PORTAL_WEB_PATHS.home} replace />;
  return <Outlet />;
}

/** Trasy portalu klienta: osoba z zespołu (każda rola poza `client`) wraca do aplikacji. Wewnątrz `RequireAuth`. */
export function RequirePortal() {
  if (!useIsPortalSession()) return <Navigate to="/dzis" replace />;
  return <Outlet />;
}

/** Trasy publiczne (logowanie, reset hasła): zalogowany użytkownik wraca do aplikacji. */
export function PublicOnly() {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <SplashScreen />;
  if (session.data?.authenticated && session.data.stage === 'active') {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={safeNext(next)} replace />;
  }
  return <Outlet />;
}

/** Tylko ścieżki wewnętrzne (bez otwartego przekierowania na obce domeny). */
export function safeNext(next: string | null | undefined): string {
  if (!next?.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return '/dzis';
  if (next.startsWith('/login') || next.startsWith('/nie-pamietam-hasla') || next.startsWith('/reset-hasla')) {
    return '/dzis';
  }
  return next;
}
